import i18n from '@/shared/i18n';

export type AppointmentStatusValue =
  | 'scheduled'
  | 'confirmed'
  | 'arrived'
  | 'completed'
  | 'cancelled'
  | 'no-show'
  | 'rescheduled';

export interface AppointmentStatusOption {
  value: AppointmentStatusValue;
}

export const APPOINTMENT_STATUS_OPTIONS: AppointmentStatusOption[] = [
  { value: 'scheduled' },
  { value: 'confirmed' },
  { value: 'arrived' },
  { value: 'completed' },
  { value: 'cancelled' },
  { value: 'no-show' },
  { value: 'rescheduled' },
];

export const TERMINAL_STATUSES: AppointmentStatusValue[] = [
  'completed',
  'cancelled',
  'no-show',
];

export const ALLOWED_TRANSITIONS: Record<AppointmentStatusValue, AppointmentStatusValue[]> = {
  scheduled: ['confirmed', 'arrived', 'cancelled', 'rescheduled'],
  confirmed: ['arrived', 'completed', 'no-show', 'cancelled', 'rescheduled'],
  // Already checked in — no-show contradicts arrivedAt / checkedInBy.
  arrived: ['completed', 'cancelled', 'rescheduled'],
  rescheduled: ['confirmed', 'arrived', 'cancelled', 'completed', 'no-show'],
  completed: [],
  cancelled: [],
  'no-show': [],
};

export type StatusColorTokens = {
  bg: string;
  border: string;
  text: string;
};

/**
 * Soft status fills for calendar/kanban. Hex (not palette.*.50) because the app
 * theme does not define tonal steps — unresolved sx paths rendered as transparent.
 * Aligned with getStatusChipColor families (primary/info/success/…).
 */
export const STATUS_COLORS: Record<AppointmentStatusValue, StatusColorTokens> = {
  scheduled: { bg: '#c5cae9', border: '#3f51b5', text: '#1a237e' },
  confirmed: { bg: '#bbdefb', border: '#2196f3', text: '#0d47a1' },
  arrived: { bg: '#b2dfdb', border: '#009688', text: '#004d40' },
  completed: { bg: '#c8e6c9', border: '#4caf50', text: '#1b5e20' },
  cancelled: { bg: '#ffcdd2', border: '#f44336', text: '#b71c1c' },
  'no-show': { bg: '#ffe0b2', border: '#ff9800', text: '#e65100' },
  rescheduled: { bg: '#e1bee7', border: '#9c27b0', text: '#4a148c' },
};

export type StatusChipColor = 'primary' | 'info' | 'success' | 'error' | 'warning' | 'secondary' | 'default';

const STATUS_LABEL_KEYS: Record<AppointmentStatusValue, string> = {
  scheduled: 'appointment:status.scheduled',
  confirmed: 'appointment:status.confirmed',
  arrived: 'appointment:status.arrived',
  completed: 'appointment:status.completed',
  cancelled: 'appointment:status.cancelled',
  'no-show': 'appointment:status.noShow',
  rescheduled: 'appointment:status.rescheduled',
};

export const isAppointmentStatus = (value: string): value is AppointmentStatusValue =>
  APPOINTMENT_STATUS_OPTIONS.some((option) => option.value === value);

export const isTerminalStatus = (status: string): boolean =>
  TERMINAL_STATUSES.includes(status as AppointmentStatusValue);

export const getNextStatuses = (status: string): AppointmentStatusValue[] => {
  if (!isAppointmentStatus(status)) return [];
  return ALLOWED_TRANSITIONS[status];
};

/**
 * Happy-path next status for one-click advance on lists.
 * Skips cancel / no-show / reschedule — those stay in the full status picker.
 */
export const FORWARD_STATUS: Partial<Record<AppointmentStatusValue, AppointmentStatusValue>> = {
  scheduled: 'confirmed',
  confirmed: 'arrived',
  arrived: 'completed',
  rescheduled: 'confirmed',
};

export const getForwardStatus = (status: string): AppointmentStatusValue | null => {
  if (!isAppointmentStatus(status)) return null;
  const next = FORWARD_STATUS[status];
  if (!next) return null;
  return ALLOWED_TRANSITIONS[status].includes(next) ? next : null;
};

export const getNextStatusOptions = (status: string): AppointmentStatusOption[] => {
  const next = getNextStatuses(status);
  return APPOINTMENT_STATUS_OPTIONS.filter((option) => next.includes(option.value));
};

/**
 * Edit-form choices follow the saved status. An unsaved pick such as cancelled
 * must not collapse the menu, because that status has no further transitions.
 */
export const getFormStatusOptions = (savedStatus: string): AppointmentStatusOption[] => {
  const next = getNextStatusOptions(savedStatus);
  const current = APPOINTMENT_STATUS_OPTIONS.find((option) => option.value === savedStatus);
  if (!current) return next;
  return [current, ...next.filter((option) => option.value !== current.value)];
};

export const getStatusLabel = (status: string): string => {
  if (isAppointmentStatus(status)) return i18n.t(STATUS_LABEL_KEYS[status] as any);
  if (!status) return i18n.t('appointment:status.unknown');
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const getStatusChipColor = (status: string): StatusChipColor => {
  switch (status) {
    case 'scheduled':
      return 'primary';
    case 'confirmed':
      return 'info';
    case 'arrived':
      return 'success';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    case 'no-show':
      return 'warning';
    case 'rescheduled':
      return 'secondary';
    default:
      return 'default';
  }
};

export const getStatusColors = (status: string): StatusColorTokens =>
  isAppointmentStatus(status) ? STATUS_COLORS[status] : STATUS_COLORS.scheduled;
